// utils/memberLanguage.js

/**
 * Utility to ensure consistent member terminology throughout the platform
 */

// Map of old terms to new terms
export const TERMINOLOGY_MAP = {
    'user': 'member',
    'users': 'members',
    'User': 'Member',
    'Users': 'Members',
    'USER': 'MEMBER',
    'USERS': 'MEMBERS',
    'user\'s': 'member\'s',
    'users\'': 'members\'',
    'User\'s': 'Member\'s',
    'Users\'': 'Members\'',
    'new user': 'new member',
    'New User': 'New Member',
    'active user': 'active member',
    'Active User': 'Active Member',
    'user base': 'community',
    'User Base': 'Community',
    'user experience': 'member experience',
    'User Experience': 'Member Experience',
    'user dashboard': 'member dashboard',
    'User Dashboard': 'Member Dashboard',
    'user profile': 'member profile',
    'User Profile': 'Member Profile',
    'user id': 'member id',
    'User ID': 'Member ID',
    'userID': 'memberID',
    'userId': 'memberId',
    'user_id': 'member_id'
  };
  
  /**
   * Replace user terminology with member terminology in a string
   * @param {string} text - Text to update
   * @returns {string} Updated text with member terminology
   */
  export const updateTerminology = (text) => {
    if (!text) return text;
    
    let updatedText = text;
    
    // Sort by length descending to replace longer phrases first
    const sortedTerms = Object.keys(TERMINOLOGY_MAP).sort((a, b) => b.length - a.length);
    
    sortedTerms.forEach(oldTerm => {
      const newTerm = TERMINOLOGY_MAP[oldTerm];
      // Use word boundaries to avoid replacing parts of words
      const regex = new RegExp(`\\b${oldTerm}\\b`, 'g');
      updatedText = updatedText.replace(regex, newTerm);
    });
    
    return updatedText;
  };
  
  /**
   * Get the correct member terminology for a specific context
   * @param {string} context - The context (e.g., 'singular', 'plural', 'possessive')
   * @returns {string} The appropriate member term
   */
  export const getMemberTerm = (context = 'singular') => {
    const terms = {
      singular: 'member',
      plural: 'members',
      possessive: 'member\'s',
      possessivePlural: 'members\'',
      capitalized: 'Member',
      capitalizedPlural: 'Members',
      title: 'Community Member',
      titlePlural: 'Community Members'
    };
    
    return terms[context] || terms.singular;
  };
  
  /**
   * Common member-related phrases
   */
  export const MEMBER_PHRASES = {
    welcome: 'Welcome to our community!',
    newMember: 'New member',
    activeMember: 'Active member',
    trustedMember: 'Trusted member',
    memberSince: 'Member since',
    memberProfile: 'Member profile',
    memberDashboard: 'Member dashboard',
    memberBenefits: 'Member benefits',
    memberResources: 'Member resources',
    memberSupport: 'Member support',
    memberCommunity: 'Member community',
    totalMembers: 'Total members',
    onlineMembers: 'Members online',
    featuredMembers: 'Featured members',
    memberTestimonials: 'Member testimonials',
    memberSuccess: 'Member success stories',
    becomeMember: 'Become a member',
    memberLogin: 'Member login',
    memberSignup: 'Member signup',
    memberLogout: 'Member logout',
    memberSettings: 'Member settings',
    memberNotifications: 'Member notifications',
    memberActivity: 'Member activity',
    memberContributions: 'Member contributions',
    memberAchievements: 'Member achievements',
    memberReferrals: 'Member referrals',
    inviteMember: 'Invite a member',
    memberDirectory: 'Member directory',
    memberSearch: 'Search members',
    memberFilters: 'Filter members',
    memberStats: 'Member statistics',
    memberGrowth: 'Member growth',
    memberRetention: 'Member retention',
    memberEngagement: 'Member engagement',
    memberSatisfaction: 'Member satisfaction',
    memberFeedback: 'Member feedback',
    memberSurvey: 'Member survey',
    memberRewards: 'Member rewards',
    memberPerks: 'Member perks',
    memberDiscounts: 'Member discounts',
    memberExclusive: 'Member exclusive',
    memberOnly: 'Members only',
    memberAccess: 'Member access',
    memberContent: 'Member content',
    memberResources: 'Member resources',
    memberTools: 'Member tools',
    memberGuide: 'Member guide',
    memberHandbook: 'Member handbook',
    memberOrientation: 'Member orientation',
    memberOnboarding: 'Member onboarding',
    memberWelcome: 'Member welcome package',
    memberBadge: 'Member badge',
    memberCard: 'Member card',
    membershipStatus: 'Membership status',
    membershipLevel: 'Membership level',
    membershipTier: 'Membership tier',
    membershipBenefits: 'Membership benefits',
    membershipPerks: 'Membership perks',
    membershipRewards: 'Membership rewards',
    membershipUpgrade: 'Upgrade membership',
    membershipRenewal: 'Membership renewal'
  };
  
  /**
   * Update component props to use member terminology
   * @param {Object} props - Component props
   * @returns {Object} Updated props with member terminology
   */
  export const updatePropsTerminology = (props) => {
    const updatedProps = {};
    
    Object.keys(props).forEach(key => {
      // Update the key if it contains 'user'
      let newKey = key;
      if (key.toLowerCase().includes('user')) {
        newKey = key.replace(/user/gi, (match) => {
          if (match === 'user') return 'member';
          if (match === 'User') return 'Member';
          if (match === 'USER') return 'MEMBER';
          return match;
        });
      }
      
      // Update string values
      if (typeof props[key] === 'string') {
        updatedProps[newKey] = updateTerminology(props[key]);
      } else {
        updatedProps[newKey] = props[key];
      }
    });
    
    return updatedProps;
  };
  
  /**
   * Helper to generate member-friendly messages
   * @param {string} type - Type of message
   * @param {Object} data - Data for the message
   * @returns {string} Member-friendly message
   */
  export const getMemberMessage = (type, data = {}) => {
    const messages = {
      welcome: `Welcome to the UniLedge community, ${data.name || 'new member'}!`,
      joinSuccess: 'You\'ve successfully joined our community!',
      profileUpdate: 'Your member profile has been updated.',
      trustIncrease: `Great job! Your contributions as a member have increased your trust score.`,
      skillVerified: 'Your skill has been verified. You\'re now eligible for more opportunities!',
      jobComplete: 'Congratulations on completing the job! You\'re making a real difference in our community.',
      resourceAdded: 'Thank you for adding your resource. It will help other members in the community.',
      groupJoined: `You\'re now a member of this group. Welcome!`,
      inviteSent: 'Invitation sent! Help grow our community by inviting more members.',
      achievementUnlocked: 'Achievement unlocked! You\'re an amazing community member.',
      supportRequest: 'Our member support team will get back to you soon.',
      feedbackThanks: 'Thank you for your feedback. It helps us serve our members better.',
      referralSuccess: 'Thanks for referring a new member to our community!',
      upgradeSuccess: 'Your membership has been upgraded. Enjoy your new benefits!',
      reminderVerify: 'Complete your member profile to unlock all community features.',
      reminderSkills: 'Add your skills to discover job opportunities and help fellow members.',
      reminderResources: 'Register your resources to earn tokens and support the community.',
      communityImpact: `Your contributions have helped ${data.count || 'many'} fellow members!`
    };
    
    return messages[type] || 'Thank you for being a valued member of our community!';
  };
  
  // Export a default object with all utilities
  export default {
    TERMINOLOGY_MAP,
    updateTerminology,
    getMemberTerm,
    MEMBER_PHRASES,
    updatePropsTerminology,
    getMemberMessage
  };